import { CalendarIcon, CloseIcon, TestTubeIcon } from '@systemeio/ui/icons';
import { BreadcrumbsData, NeutralButton, toast } from '@systemeio/ui';
import { BadRequest, PayloadTooLarge } from '@systemeio/util';
import { campaignStepDefaultErrors } from 'modules/campaigns/campaign-step-single/constants/campaign-step-default-errors';
import { setCampaignStepErrors } from 'modules/campaigns/campaign-step-single/utils/set-campaign-step-errors';
import { ActivateCampaignStepModal } from 'modules/campaigns/campaign-steps/components/activate-campaign-step/activate-campaign-step-modal-with-button';
import { ActivateCampaignStepWithoutButton } from 'modules/campaigns/campaign-steps/components/activate-campaign-step/activate-campaign-step-without-button';
import { transformCampaignStepDataToActivateData } from 'modules/campaigns/campaign-steps/components/activate-campaign-step/utils';
import { useCampaignStepData } from 'modules/campaigns/campaign-steps/hooks/use-campaign-step-data';
import { useUpdateCampaignStep } from 'modules/campaigns/campaign-steps/hooks/use-update-campaign-step';
import { CampaignStepErrorResponseContentInterface } from 'modules/campaigns/campaign-steps/types/campaign-step-error-response-interface';
import { CampaignStepActivateData, CampaignStepId } from 'modules/campaigns/campaign-steps/types/campaign-step-interface';
import { CampaignStepViewInterface } from 'modules/campaigns/campaign-steps/types/campaign-step-view-interface';
import { getCampaignStepsListNextjsUrl } from 'modules/campaigns/campaign-steps/utils/get-campaign-steps-list-nextjs-url';
import { transformToCampaignStepData } from 'modules/campaigns/campaign-steps/utils/transform-to-campaign-step-data';
import { CampaignId } from 'modules/campaigns/types/campaign-interface';
import { getCampaignUrl } from 'modules/campaigns/utils/get-campaign-url';
import { getCampaignsListNextjsUrl } from 'modules/campaigns/utils/get-campaigns-list-url';
import { SendTestEmailModal } from 'modules/mailing/components/send-test-email-modal';
import React, { useEffect, useState } from 'react';
import BreadcrumbsDashboard from 'shared/components/breadcrumbs';
import { ButtonWithDropdown } from 'shared/components/buttin-with-dropdown/button-with-dropdown';
import { ButtonWithDropdownTypeEnum } from 'shared/components/buttin-with-dropdown/enums/button-with-dropdown-type-enum';
import DashboardMailEditor from 'shared/components/dashboard-mail-editor';
import { useMailingState } from 'shared/components/dashboard-mail-editor/hooks/use-mailing-state';
import { useUnsavedEmail } from 'shared/components/dashboard-mail-editor/hooks/use-unsaved-email';
import { TempStateType } from 'shared/components/dashboard-mail-editor/types/temp-state-type';
import { getMailingUpdateData } from 'shared/components/dashboard-mail-editor/utils/get-mailing-update-data';
import { validateEmailBeforeSave } from 'shared/components/dashboard-mail-editor/utils/validate-email-before-save';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { ConfirmationModalBeforeExit } from 'shared/confirmation-modal-before-exit/component';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { deepCompare } from 'shared/utils/deepCompare';
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
interface CampaignStepSinglePropsInterface {
  campaignId: CampaignId;
  campaignStepId: CampaignStepId;
  campaignStepData: CampaignStepViewInterface;
}
export function CampaignStepSingle({
  campaignId,
  campaignStepId,
  campaignStepData
}: CampaignStepSinglePropsInterface) {
  const {
    t
  } = useLocoTranslation();
  const {
    fetcherUpdateCampaignStep
  } = useUpdateCampaignStep(campaignStepId);
  const {
    mutateCampaignStepData,
    manualFetcher
  } = useCampaignStepData({
    campaignStepId
  });
  const {
    revalidateModules
  } = useRevalidateModules();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const {
    isFetchingChooseEmailTemplate,
    onChangeEmail,
    tempState,
    setAttachmentsTempState,
    attachmentsTempState,
    errors,
    setErrors,
    setDefaultErrors
  } = useMailingState({
    mailing: campaignStepData.mailing,
    onSave: data => onSave(data, activateData, {
      throwError: true
    }),
    defaultErrors: campaignStepDefaultErrors
  });
  const [activateData, setActivateData] = useState<CampaignStepActivateData | null>(transformCampaignStepDataToActivateData(campaignStepData));
  const [isDataSaved, setIsDataSaved] = useState<boolean>(true);
  const {
    UnsavedEmailConfirmationModal,
    removeDbRecord
  } = useUnsavedEmail({
    mailingId: campaignStepData?.mailing.id,
    initialBody: campaignStepData?.mailing.currentEmail.body,
    currentBody: tempState.body,
    showImmediately: true,
    onPaste: data_0 => onChangeEmail({
      ...tempState,
      body: data_0
    })
  });
  useEffect(() => {
    setActivateData(transformCampaignStepDataToActivateData(campaignStepData));
  }, [campaignStepData]);
  const isLoading = isFetching || isFetchingChooseEmailTemplate;
  const breadcrumbsData: BreadcrumbsData[] = [{
    caption: t('dashboard.campaign.title_plural'),
    href: getCampaignsListNextjsUrl(),
    isStatic: true
  }, {
    caption: campaignStepData.campaign.name,
    href: getCampaignUrl(campaignId)
  }, {
    caption: campaignStepData.mailing.currentEmail.subject
  }];
  useEffect(() => {
    setIsDataSaved(deepCompare(transformToCampaignStepData({
      tempState,
      activateData,
      campaignStepData,
      attachmentsTempState
    }), campaignStepData));
  }, [activateData, attachmentsTempState, campaignStepData, tempState]);
  const onSave = async (newData: TempStateType, tempActivateData: CampaignStepActivateData | null, options?: {
    throwError?: boolean;
  }) => {
    try {
      setDefaultErrors();
      validateEmailBeforeSave(newData.fromEmail, setErrors, t);
      setIsFetching(true);
      const activatePayloadData = campaignStepData.isActive ? activateData : tempActivateData;
      const responseData = await fetcherUpdateCampaignStep({
        mailing: {
          currentEmail: getMailingUpdateData(newData, attachmentsTempState)
        },
        ...activatePayloadData
      });
      await mutateCampaignStepData(transformToCampaignStepData({
        tempState: newData,
        activateData: transformCampaignStepDataToActivateData(responseData),
        attachmentsTempState: responseData.mailing.currentEmail.attachments,
        campaignStepData: responseData
      }), false);
      toast.success(t('global.changes_saved'));
      setIsFetching(false);
      await removeDbRecord();
      await revalidateModules([RequestModuleEnum.CampaignStep]);
    } catch (e) {
      setIsFetching(false);
      toast.error(t('global.error'));
      if (e instanceof BadRequest) {
        const errors_0 = e.errors as CampaignStepErrorResponseContentInterface;
        const campaignStepErrors = !Array.isArray(errors_0.fields) ? errors_0.fields : undefined;
        if (campaignStepErrors) {
          setCampaignStepErrors(setErrors, campaignStepErrors);
        }
        if (e.errors.common) {
          const commonErrors = e.errors.common;
          setErrors(prev => {
            return {
              ...prev,
              common: commonErrors
            };
          });
        }
      } else if (e instanceof PayloadTooLarge) {
        toast.error(t('core.error.payload_to_large_error_message'));
      }
      if (options?.throwError) {
        throw e;
      }
    }
  };
  const SideBar = campaignStepData.isActive ? {
    title: t('dashboard.campaign_step_editor.activate_settings_title'),
    Component: <ActivateCampaignStepWithoutButton className="max-w-none" onChange={setActivateData} initialData={activateData} campaignStepId={campaignStepId} campaignId={campaignId} isPreFetching={isLoading} errors={errors} />,
    showErrorOutline: !!errors.excludedWeekdays
  } : undefined;
  const mutateSenderEmail = async (fromEmail: string) => {
    try {
      await mutateCampaignStepData(data_1 => {
        if (!data_1) return data_1;
        return {
          ...data_1,
          mailing: {
            ...data_1.mailing,
            currentEmail: {
              ...data_1.mailing.currentEmail,
              fromEmail
            }
          }
        };
      }, false);
    } catch {}
  };
  const activateCampaignStep = async (activateData_0: CampaignStepActivateData) => {
    let stepBefore: CampaignStepViewInterface['stepBefore'] = null;
    if (activateData_0.stepBefore) {
      const stepBeforeData = await manualFetcher(activateData_0.stepBefore);
      if (stepBeforeData) {
        stepBefore = {
          id: activateData_0.stepBefore,
          mailing: stepBeforeData.mailing
        };
      }
    }
    try {
      setActivateData(activateData_0);
      await mutateCampaignStepData(transformToCampaignStepData({
        activateData: activateData_0,
        campaignStepData: campaignStepData && {
          ...campaignStepData,
          stepBefore,
          isActive: true
        },
        attachmentsTempState: campaignStepData?.mailing.currentEmail.attachments
      }), false);
      toast.success(t('global.changes_saved'));
      await revalidateModules([RequestModuleEnum.CampaignStep]);
    } catch (e_0) {}
  };
  return <>
      <div className={`flex flex-col gap-5`}>
        <div className="flex flex-col items-center justify-between gap-5 sm:flex-row">
          <BreadcrumbsDashboard data={breadcrumbsData} isFetching={isLoading} />
          <div className={'flex gap-3'}>
            <ButtonWithDropdown options={[{
            name: 'test',
            type: ButtonWithDropdownTypeEnum.Button,
            menuItem: {
              title: t('dashboard.campaign_step_editor.save_and_send_test'),
              icon: <TestTubeIcon className={'fill-text-primary h-[18px] w-[18px]'} />
            },
            onActionRender: (opened, onClose, key) => <SendTestEmailModal key={key} mailingId={campaignStepData.mailing.id} onSave={() => onSave(tempState, activateData, {
              throwError: true
            })} onClose={onClose} opened={opened} />
          }, {
            name: 'activate',
            type: ButtonWithDropdownTypeEnum.Button,
            menuItem: {
              title: t('dashboard.campaign_step_editor.save_and_publish'),
              icon: <CalendarIcon className={'fill-text-primary h-[18px] w-[18px]'} />
            },
            shouldRender: !campaignStepData.isActive,
            onActionRender: (opened_0, onClose_0, key_0) => <ActivateCampaignStepModal key={key_0} onOpen={() => onSave(tempState, activateData)} onClose={onClose_0} opened={opened_0} initialData={activateData} disabled={isLoading} campaignId={campaignId} campaignStepId={campaignStepId} onConfirm={activateCampaignStep} senderEmail={campaignStepData?.mailing.currentEmail.fromEmail} onSenderEmailChange={({
              fromEmail: fromEmail_0
            }) => fromEmail_0 && mutateSenderEmail(fromEmail_0)} />
          }]} disabled={isLoading} onClick={() => onSave(tempState, activateData)} title={campaignStepData.isActive ? t('global.save') : t('global.save_draft')} isFetching={isFetching} />
            <NeutralButton as={LinkWithoutPrefetch} href={getCampaignStepsListNextjsUrl(campaignId)}>
              <CloseIcon className={'h-[20px] w-[20px]'} />
              <span className="hidden lg:flex">{t('global.exit')}</span>
            </NeutralButton>
          </div>
        </div>
        <DashboardMailEditor isSwitchEditorButtonShown isPreFetching={isLoading} onChangeEmail={onChangeEmail} attachmentsTempState={attachmentsTempState} setAttachmentsTempState={setAttachmentsTempState} errors={errors} tempState={tempState} sidebarItemBlock={SideBar} mailingId={campaignStepData?.mailing.id} />
      </div>
      <ConfirmationModalBeforeExit confirmationText={t('dashboard.campaign_step_editor.confirmation_message_before_exit')} hideModal={isDataSaved} />
      {UnsavedEmailConfirmationModal}
    </>;
}